import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [exportUrl, setExportUrl] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [tab, setTab] = useState("today");

  const refresh = async () => {
    // CODEREVIEW-M2: Load each admin panel independently. Previously we used
    // Promise.all — one failing endpoint (e.g. transient 500 on /reviews)
    // rejected the whole batch, no state was set, and the entire dashboard
    // rendered empty with no error shown. Now each slice sets on success and
    // toasts on failure, so a stuck panel never blanks the working ones.
    const endpoints = [
      ["/admin/stats",           setStats],
      ["/admin/bookings",        setBookings],
      ["/admin/blocked-dates",   setBlocks],
      ["/admin/inquiries",       setInquiries],
      ["/admin/settings",        setSettings],
      ["/admin/pricing-seasons", setSeasons],
      ["/admin/ical/feeds",      setFeeds],
      ["/admin/ical/export-url", setExportUrl],
      ["/admin/arrivals",        setArrivals],
      ["/admin/referrals",       setReferrals],
      ["/admin/newsletter",      setSubscribers],
      ["/admin/waitlist",        setWaitlist],
      ["/admin/reviews",         setReviews],
      ["/admin/reminders",       setReminders],
    ];
    const results = await Promise.allSettled(endpoints.map(([url]) => api.get(url)));
    const failures = [];
    results.forEach((r, i) => {
      const [url, setter] = endpoints[i];
      if (r.status === "fulfilled") {
        setter(r.value.data);
      } else {
        failures.push(url);
        console.warn(`admin refresh: ${url} failed`, r.reason);
      }
    });
    if (failures.length > 0) {
      toast.error(`${failures.length} panel${failures.length > 1 ? "s" : ""} failed to load — check console`);
    }
  };

  useEffect(() => {
    api.get("/auth/me").then(r => { setMe(r.data); refresh(); })
      .catch(() => nav("/admin/login"));
  }, []);

  function logout() { localStorage.removeItem("ithaca_token"); nav("/admin/login"); }
  if (!me) return <div className="min-h-screen flex items-center justify-center text-ionian/60">Loading…</div>;

  return (
    <div className="min-h-screen bg-stone_white">
      <div className="border-b border-soft_border bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <div className="overline">Owner dashboard</div>
            <div className="font-serif text-2xl mt-1">Ithaca Garden Villa</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ionian/60">{me.email}</span>
            <Button data-testid="admin-logout" variant="outline" onClick={logout}>Sign out</Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
            <StatCard label="Total bookings" value={stats.total_bookings} />
            <StatCard label="Paid" value={stats.paid_bookings} />
            <StatCard label="Pending" value={stats.pending_bookings} />
            <StatCard label="Revenue (€)" value={stats.revenue.toFixed(0)} />
            <StatCard label="Enquiries" value={stats.inquiries} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="seasons" data-testid="tab-seasons">Seasons</TabsTrigger>
            <TabsTrigger value="sync" data-testid="tab-sync">Sync</TabsTrigger>
            <TabsTrigger value="arrival" data-testid="tab-arrival">Pre-arrival</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
            <TabsTrigger value="mailing" data-testid="tab-mailing">Mailing list</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Waitlist</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Pricing</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Enquiries</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-6">
            <RemindersPanel reminders={reminders} onRefresh={refresh} />
            <ArrivalsToday arrivals={arrivals} onRefresh={refresh} />
          </TabsContent>
          <TabsContent value="bookings" className="mt-6"><BookingsTable bookings={bookings} onRefresh={refresh} /></TabsContent>
          <TabsContent value="calendar" className="mt-6"><BlockedDates blocks={blocks} onRefresh={refresh} /></TabsContent>
          <TabsContent value="seasons" className="mt-6"><Seasons seasons={seasons} onRefresh={refresh} /></TabsContent>
          <TabsContent value="sync" className="mt-6"><SyncPanel feeds={feeds} exportUrl={exportUrl} onRefresh={refresh} /></TabsContent>
          <TabsContent value="arrival" className="mt-6">{settings && <ArrivalPanel settings={settings} onRefresh={refresh} />}</TabsContent>
          <TabsContent value="referrals" className="mt-6"><ReferralsPanel referrals={referrals} onRefresh={refresh} /></TabsContent>
          <TabsContent value="mailing" className="mt-6"><MailingListPanel subscribers={subscribers} onRefresh={refresh} /></TabsContent>
          <TabsContent value="waitlist" className="mt-6"><WaitlistPanel waitlist={waitlist} onRefresh={refresh} /></TabsContent>
          <TabsContent value="reviews" className="mt-6"><ReviewsPanel reviews={reviews} onRefresh={refresh} /></TabsContent>
          <TabsContent value="settings" className="mt-6">{settings && <SettingsForm settings={settings} onRefresh={refresh} />}</TabsContent>
          <TabsContent value="inquiries" className="mt-6"><Inquiries inquiries={inquiries} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-soft_border bg-white p-6">
      <div className="overline">{label}</div>
      <div className="font-serif text-4xl mt-2 text-ionian">{value}</div>
    </div>
  );
}

function BookingsTable({ bookings, onRefresh }) {
  async function updateStatus(id, status) {
    try { await api.patch(`/admin/bookings/${id}`, { status }); toast.success("Updated"); onRefresh(); }
    catch { toast.error("Failed"); }
  }
  async function resend(id) {
    try { await api.post(`/admin/bookings/${id}/resend-email`); toast.success("Confirmation email sent"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function sendArrival(id) {
    try { await api.post(`/admin/bookings/${id}/send-arrival`); toast.success("Pre-arrival email sent"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function sendRescue(id) {
    if (!window.confirm("Send a rescue email nudging this guest to complete their booking?")) return;
    try { await api.post(`/admin/bookings/${id}/send-rescue`); toast.success("Rescue email sent"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function refundDeposit(b) {
    if (!window.confirm(`Refund the €${Number(b.deposit_amount).toFixed(0)} damage deposit to ${b.guest_name}? This action is irreversible via Stripe.`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund-deposit`);
      toast.success(`€${data.amount.toFixed(0)} refunded via Stripe`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this booking?")) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); onRefresh(); }
    catch { toast.error("Failed"); }
  }
  const badgeVariant = (s) => ({ paid: "default", pending: "secondary", failed: "destructive", confirmed: "default" }[s] || "outline");
  return (
    <div className="border border-soft_border bg-white">
      <Table data-testid="bookings-table">
        <TableHeader>
          <TableRow><TableHead>Guest</TableHead><TableHead>Dates</TableHead><TableHead>Nights</TableHead><TableHead>Guests</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {bookings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-ionian/50 py-10">No bookings yet.</TableCell></TableRow>}
          {bookings.map(b => (
            <TableRow key={b.id}>
              <TableCell><div className="font-medium">{b.guest_name}</div><div className="text-xs text-ionian/60">{b.guest_email}</div></TableCell>
              <TableCell>{b.checkin} → {b.checkout}</TableCell>
              <TableCell>{b.nights}</TableCell>
              <TableCell>{b.guests}</TableCell>
              <TableCell>€{Number(b.amount).toFixed(2)}</TableCell>
              <TableCell><Badge variant={badgeVariant(b.status)}>{b.status}</Badge></TableCell>
              <TableCell className="text-right space-x-2">
                {b.status !== "paid" && <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "paid")}>Mark paid</Button>}
                {b.status === "paid" && <Button size="sm" variant="outline" onClick={() => resend(b.id)} data-testid={`resend-${b.id}`}>{b.email_sent ? "Resend email" : "Send email"}</Button>}
                {b.status === "paid" && <Button size="sm" variant="outline" onClick={() => sendArrival(b.id)} data-testid={`arrival-${b.id}`} title={b.arrival_email_sent ? `Sent ${b.arrival_email_sent_at ? new Date(b.arrival_email_sent_at).toLocaleString() : ""}` : "Send the pre-arrival welcome now"}>{b.arrival_email_sent ? "Resend arrival" : "Send arrival"}</Button>}
                {(b.status === "pending" || b.status === "expired") && b.guest_email && (
                  <Button size="sm" variant="outline" onClick={() => sendRescue(b.id)} data-testid={`rescue-${b.id}`} title={b.rescue_email_sent ? `Sent ${b.rescue_email_sent_at ? new Date(b.rescue_email_sent_at).toLocaleString() : ""}` : "Nudge this guest to complete their booking"}>{b.rescue_email_sent ? "Resend rescue" : "Send rescue"}</Button>
                )}
                {b.status === "paid" && b.deposit_amount > 0 && !b.deposit_refunded && (
                  <Button size="sm" variant="outline" onClick={() => refundDeposit(b)} data-testid={`refund-${b.id}`}>Refund deposit</Button>
                )}
                {b.deposit_refunded && (
                  <span className="text-xs text-olive italic" title={b.deposit_refund_id}>Deposit refunded</span>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(b.id)}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BlockedDates({ blocks, onRefresh }) {
  const [start, setStart] = useState(""); const [end, setEnd] = useState(""); const [reason, setReason] = useState("");
  async function add() {
    if (!start || !end) return toast.error("Both dates required");
    try {
      await api.post("/admin/blocked-dates", { start_date: start, end_date: end, reason });
      setStart(""); setEnd(""); setReason(""); onRefresh(); toast.success("Dates blocked");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    try { await api.delete(`/admin/blocked-dates/${id}`); onRefresh(); toast.success("Removed"); } catch { toast.error("Failed"); }
  }
  return (
    <div className="border border-soft_border bg-white p-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div><Label>Start date</Label><Input data-testid="block-start" type="date" value={start} onChange={e=>setStart(e.target.value)} /></div>
        <div><Label>End date</Label><Input data-testid="block-end" type="date" value={end} onChange={e=>setEnd(e.target.value)} /></div>
        <div><Label>Reason (optional)</Label><Input data-testid="block-reason" value={reason} onChange={e=>setReason(e.target.value)} /></div>
        <Button data-testid="block-add" onClick={add} className="btn-ionian">Block dates</Button>
      </div>
      <div className="mt-6">
        <Table>
          <TableHeader><TableRow><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Reason</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {blocks.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-ionian/50 py-6">No blocked ranges.</TableCell></TableRow>}
            {blocks.map(b => (
              <TableRow key={b.id}>
                <TableCell>{b.start_date}</TableCell><TableCell>{b.end_date}</TableCell>
                <TableCell>{b.reason || "—"}</TableCell>
                <TableCell className="text-right">
                  {!b.source_feed_id && <Button size="sm" variant="ghost" onClick={()=>remove(b.id)}>Remove</Button>}
                  {b.source_feed_id && <span className="text-xs text-ionian/50 italic">from iCal</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Seasons({ seasons, onRefresh }) {
  const [f, setF] = useState({ name: "", start_date: "", end_date: "", nightly_rate: "" });
  async function add() {
    if (!f.name || !f.start_date || !f.end_date || !f.nightly_rate) return toast.error("Fill all fields");
    try {
      await api.post("/admin/pricing-seasons", { ...f, nightly_rate: Number(f.nightly_rate) });
      setF({ name: "", start_date: "", end_date: "", nightly_rate: "" });
      onRefresh(); toast.success("Season added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    try { await api.delete(`/admin/pricing-seasons/${id}`); onRefresh(); toast.success("Removed"); } catch { toast.error("Failed"); }
  }
  return (
    <div className="border border-soft_border bg-white p-6">
      <div className="mb-4 text-sm text-ionian/70">
        Override the default nightly rate for specific date ranges (e.g. Peak July/August). Overlapping seasons: the one starting <em>later</em> wins.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
        <div><Label>Name</Label><Input data-testid="season-name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Peak summer" /></div>
        <div><Label>Start date</Label><Input data-testid="season-start" type="date" value={f.start_date} onChange={e=>setF({...f,start_date:e.target.value})} /></div>
        <div><Label>End date</Label><Input data-testid="season-end" type="date" value={f.end_date} onChange={e=>setF({...f,end_date:e.target.value})} /></div>
        <div><Label>Nightly rate (€)</Label><Input data-testid="season-rate" type="number" value={f.nightly_rate} onChange={e=>setF({...f,nightly_rate:e.target.value})} placeholder="1200" /></div>
        <Button data-testid="season-add" onClick={add} className="btn-ionian">Add season</Button>
      </div>
      <div className="mt-6">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Rate</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {seasons.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-ionian/50 py-6">No seasonal overrides. Default rate applies to all nights.</TableCell></TableRow>}
            {seasons.map(s => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.start_date}</TableCell>
                <TableCell>{s.end_date}</TableCell>
                <TableCell>€{Number(s.nightly_rate).toFixed(0)}</TableCell>
                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={()=>remove(s.id)}>Remove</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SyncPanel({ feeds, exportUrl, onRefresh }) {
  const [f, setF] = useState({ name: "", url: "", source: "airbnb" });
  const [refreshing, setRefreshing] = useState(false);

  async function add() {
    if (!f.name || !f.url) return toast.error("Name and URL required");
    try {
      await api.post("/admin/ical/feeds", f);
      setF({ name: "", url: "", source: "airbnb" });
      onRefresh(); toast.success("Feed added — click Sync now to pull dates");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    if (!window.confirm("Remove feed and its blocked dates?")) return;
    try { await api.delete(`/admin/ical/feeds/${id}`); onRefresh(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  }
  async function syncAll() {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/ical/refresh");
      toast.success(`Synced ${data.results.length} feed(s)`);
      onRefresh();
    } catch { toast.error("Sync failed"); }
    setRefreshing(false);
  }
  async function copy(text) {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch {}
  }

  return (
    <div className="space-y-8">
      {/* Export */}
      <div className="border border-soft_border bg-white p-6">
        <div className="overline">Publish your calendar</div>
        <h3 className="font-serif text-2xl mt-2 mb-4">Airbnb / Booking.com iCal Export</h3>
        <p className="text-sm text-ionian/70 mb-4">
          Paste this URL into Airbnb → Calendar → Sync calendars, or Booking.com → Rates & Availability → Sync calendars.
          This is a private link — treat it like a password.
        </p>
        <div className="flex gap-2 items-center">
          <Input data-testid="ical-export-url" value={exportUrl?.url || ""} readOnly className="font-mono text-xs" />
          <Button data-testid="ical-copy" variant="outline" onClick={() => copy(exportUrl?.url)}>Copy</Button>
        </div>
      </div>

      {/* Import feeds */}
      <div className="border border-soft_border bg-white p-6">
        <div className="overline">Import external calendars</div>
        <h3 className="font-serif text-2xl mt-2 mb-4">Airbnb / Booking.com iCal Import</h3>
        <p className="text-sm text-ionian/70 mb-4">
          Add the iCal URLs Airbnb and Booking.com give you. On sync, we'll pull their bookings into your Calendar as blocked dates.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div><Label>Feed name</Label><Input data-testid="feed-name" value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Airbnb - Ithaca villa" /></div>
          <div className="md:col-span-2"><Label>iCal URL</Label><Input data-testid="feed-url" value={f.url} onChange={e=>setF({...f,url:e.target.value})} placeholder="https://…airbnb.com/calendar/ical/…" /></div>
          <div><Label>Source</Label>
            <select data-testid="feed-source" value={f.source} onChange={e=>setF({...f,source:e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-transparent">
              <option value="airbnb">Airbnb</option>
              <option value="booking">Booking.com</option>
              <option value="vrbo">Vrbo</option>
              <option value="external">Other</option>
            </select>
          </div>
          <Button data-testid="feed-add" onClick={add} className="btn-ionian">Add feed</Button>
        </div>

        <div className="mt-6 flex justify-end">
          <Button data-testid="feed-sync-all" onClick={syncAll} disabled={refreshing || feeds.length === 0} className="btn-brass">
            {refreshing ? "Syncing…" : "Sync now"}
          </Button>
        </div>

        <div className="mt-4">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Source</TableHead><TableHead>Last synced</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {feeds.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-ionian/50 py-6">No feeds yet.</TableCell></TableRow>}
              {feeds.map(x => (
                <TableRow key={x.id}>
                  <TableCell className="font-medium">{x.name}</TableCell>
                  <TableCell><Badge variant="outline">{x.source}</Badge></TableCell>
                  <TableCell>{x.last_synced ? new Date(x.last_synced).toLocaleString() : "—"}</TableCell>
                  <TableCell>{x.last_status === "ok" ? <span className="text-olive">✓ {x.last_events} events</span> : x.last_status === "error" ? <span className="text-terracotta" title={x.last_error}>error</span> : <span className="text-ionian/50">—</span>}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={()=>remove(x.id)}>Remove</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Synced Blocks Debug */}
      <SyncedBlocksDebug feeds={feeds} />

      <InstagramFeedCard />
    </div>
  );
}

function SyncedBlocksDebug({ feeds }) {
  const [blocks, setBlocks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all | airbnb | booking | manual

  const feedMap = useMemo(() => Object.fromEntries((feeds || []).map(f => [f.id, f])), [feeds]);

  async function load() {
    try {
      const { data } = await api.get("/admin/blocked-dates");
      setBlocks(data || []);
    } catch { toast.error("Could not load synced blocks"); }
    setLoaded(true);
  }
  useEffect(() => { if (open && !loaded) load(); }, [open, loaded]);  // eslint-disable-line

  function classify(b) {
    if (!b.source_feed_id) return "manual";
    const feed = feedMap[b.source_feed_id];
    return (feed?.source || "external").toLowerCase();
  }
  function nights(b) {
    try {
      const a = new Date(b.start_date + "T00:00:00");
      const c = new Date(b.end_date + "T00:00:00");
      return Math.max(0, Math.round((c - a) / 86400000));
    } catch { return 0; }
  }

  const filtered = blocks.filter(b => filter === "all" || classify(b) === filter);
  const grouped = filtered.reduce((acc, b) => {
    const src = classify(b);
    (acc[src] = acc[src] || []).push(b);
    return acc;
  }, {});
  const totalNights = filtered.reduce((s, b) => s + nights(b), 0);

  return (
    <div className="border border-soft_border bg-white p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Debug</div>
          <h3 className="font-serif text-2xl mt-2">Synced blocks — raw view</h3>
          <p className="text-sm text-ionian/70 mt-2">
            Every date range currently blocked in the calendar, grouped by source. Useful to confirm what Airbnb / Booking.com actually sent us.
          </p>
        </div>
        <Button
          data-testid="synced-blocks-toggle"
          variant="outline"
          onClick={() => { setOpen(o => !o); if (!open) { setLoaded(false); } }}
        >
          {open ? "Hide" : "Show synced blocks"}
        </Button>
      </div>

      {open && (
        <div className="mt-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {["all", "airbnb", "booking", "manual"].map(k => (
              <Button
                key={k}
                size="sm"
                variant={filter === k ? "default" : "outline"}
                onClick={() => setFilter(k)}
                data-testid={`blocks-filter-${k}`}
              >
                {k === "all" ? "All" : k[0].toUpperCase() + k.slice(1)}
                {" · "}
                {k === "all" ? blocks.length : (blocks.filter(b => classify(b) === k).length)}
              </Button>
            ))}
            <div className="ml-auto text-xs text-ionian/60 self-center">
              {filtered.length} block{filtered.length === 1 ? "" : "s"} · {totalNights} night{totalNights === 1 ? "" : "s"} total
            </div>
          </div>

          {!loaded && <p className="text-sm text-ionian/50">Loading…</p>}
          {loaded && filtered.length === 0 && (
            <p className="text-sm text-ionian/50 italic py-6 text-center">
              No blocks for this source. If you expected reservations here, they may not exist on the OTA yet.
            </p>
          )}
          {loaded && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead>Feed name</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Nights</TableHead>
                    <TableHead>Reason / iCal event</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(grouped).map(([src, list]) =>
                    list.sort((a,b) => a.start_date.localeCompare(b.start_date)).map(b => (
                      <TableRow key={b.id} data-testid={`block-row-${b.id}`}>
                        <TableCell><Badge variant="outline" className="capitalize">{src}</Badge></TableCell>
                        <TableCell className="text-sm">{feedMap[b.source_feed_id]?.name || <span className="italic text-ionian/40">manual</span>}</TableCell>
                        <TableCell className="tabular-nums">{b.start_date}</TableCell>
                        <TableCell className="tabular-nums">{b.end_date}</TableCell>
                        <TableCell className="tabular-nums">{nights(b)}</TableCell>
                        <TableCell className="text-xs text-ionian/70 max-w-md truncate" title={b.reason}>{b.reason || "—"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function InstagramFeedCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/instagram/status");
      setStatus(data);
    } catch (e) { /* silent */ }
  }
  useEffect(() => { load(); }, []);

  async function refreshFeed() {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/instagram/refresh");
      if (data.ok) {
        toast.success(`Instagram — pulled ${data.count} post(s)`);
      } else {
        toast.error(data.error || "Refresh failed");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  }

  const cache = status?.cache_meta;
  const tokenMeta = status?.token_meta;
  const configured = status?.configured;
  const cachedAt = cache?.cached_at ? new Date(cache.cached_at) : null;
  const tokenExp = tokenMeta?.expires_at ? new Date(tokenMeta.expires_at) : null;
  const cacheAgo = cachedAt ? Math.round((Date.now() - cachedAt.getTime()) / 60000) : null;

  return (
    <div className="border border-soft_border bg-white p-6" data-testid="admin-ig-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2">
            Instagram feed
            {configured ? (
              <Badge className="bg-olive/15 text-olive border-olive/40">Live</Badge>
            ) : (
              <Badge variant="outline" className="text-terracotta border-terracotta">Not configured</Badge>
            )}
          </div>
          <h3 className="font-serif text-2xl mt-2">The 3-tile gallery on the home page</h3>
          <p className="text-sm text-ionian/70 mt-2 max-w-xl">
            Pulled from <span className="font-mono">@ithacagardenvilla</span> every 30 minutes. Click refresh
            below if you just posted and want to pull the new shot into the site immediately.
          </p>
        </div>
        <Button
          data-testid="ig-refresh-btn"
          onClick={refreshFeed}
          disabled={busy}
          className="btn-brass"
        >
          {busy ? "Refreshing…" : "Refresh now"}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-soft_border">
        <div>
          <div className="overline">Cached posts</div>
          <div className="font-serif text-xl mt-1" data-testid="ig-cache-count">
            {cache ? "up to date" : "empty"}
          </div>
          <div className="text-xs text-ionian/60 mt-1">
            {cacheAgo == null
              ? "Never fetched yet"
              : cacheAgo < 60
                ? `Refreshed ${cacheAgo} min ago`
                : `Refreshed ${Math.round(cacheAgo / 60)} h ago`}
          </div>
        </div>
        <div>
          <div className="overline">Token expiry</div>
          <div className="font-serif text-xl mt-1" data-testid="ig-token-expiry">
            {tokenExp ? tokenExp.toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" }) : "seed only"}
          </div>
          <div className="text-xs text-ionian/60 mt-1">
            {tokenExp
              ? `Auto-refreshes 20 days before expiry`
              : "Will refresh on next scheduled sync"}
          </div>
        </div>
        <div>
          <div className="overline">Setup</div>
          <div className="text-xs mt-2 space-y-0.5" data-testid="ig-setup-flags">
            <div>{status?.ig_user_id_set ? "✓" : "✗"} IG user ID</div>
            <div>{status?.long_lived_token_seeded ? "✓" : "✗"} Long-lived token</div>
            <div>{status?.app_id_set ? "✓" : "✗"} Meta App ID</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsForm({ settings, onRefresh }) {
  const [f, setF] = useState(settings);
  async function save() {
    try {
      await api.patch("/admin/settings", {
        villa_name: f.villa_name, location: f.location, tagline: f.tagline,
        nightly_rate: Number(f.nightly_rate), cleaning_fee: Number(f.cleaning_fee),
        min_nights: Number(f.min_nights), max_guests: Number(f.max_guests),
      });
      toast.success("Saved"); onRefresh();
    } catch (e) { toast.error("Failed"); }
  }
  return (
    <div className="border border-soft_border bg-white p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div><Label>Villa name</Label><Input data-testid="settings-name" value={f.villa_name} onChange={e=>setF({...f,villa_name:e.target.value})} /></div>
      <div><Label>Location</Label><Input value={f.location} onChange={e=>setF({...f,location:e.target.value})} /></div>
      <div className="md:col-span-2"><Label>Tagline</Label><Input value={f.tagline || ""} onChange={e=>setF({...f,tagline:e.target.value})} /></div>
      <div><Label>Default nightly rate (€)</Label><Input data-testid="settings-rate" type="number" value={f.nightly_rate} onChange={e=>setF({...f,nightly_rate:e.target.value})} /></div>
      <div><Label>Cleaning fee (€)</Label><Input type="number" value={f.cleaning_fee} onChange={e=>setF({...f,cleaning_fee:e.target.value})} /></div>
      <div><Label>Minimum nights</Label><Input type="number" value={f.min_nights} onChange={e=>setF({...f,min_nights:e.target.value})} /></div>
      <div><Label>Max guests</Label><Input type="number" value={f.max_guests} onChange={e=>setF({...f,max_guests:e.target.value})} /></div>
      <div className="md:col-span-2"><Button data-testid="settings-save" onClick={save} className="btn-ionian">Save changes</Button></div>
    </div>
  );
}

function Inquiries({ inquiries }) {
  return (
    <div className="border border-soft_border bg-white">
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Message</TableHead><TableHead>Received</TableHead></TableRow></TableHeader>
        <TableBody>
          {inquiries.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-ionian/50 py-10">No enquiries yet.</TableCell></TableRow>}
          {inquiries.map(i => (
            <TableRow key={i.id}>
              <TableCell className="font-medium">{i.name}</TableCell>
              <TableCell>{i.email}</TableCell>
              <TableCell className="max-w-md whitespace-normal">{i.message}</TableCell>
              <TableCell>{new Date(i.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ArrivalPanel({ settings, onRefresh }) {
  const [f, setF] = useState({
    arrival_email_enabled: settings.arrival_email_enabled ?? true,
    arrival_email_lead_hours: settings.arrival_email_lead_hours ?? 48,
    arrival_email_subject: settings.arrival_email_subject || "",
    arrival_email_html: settings.arrival_email_html || "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        arrival_email_enabled: !!f.arrival_email_enabled,
        arrival_email_lead_hours: Number(f.arrival_email_lead_hours) || 48,
        arrival_email_subject: f.arrival_email_subject,
        arrival_email_html: f.arrival_email_html,
      });
      toast.success("Pre-arrival template saved");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  }
  async function doPreview() {
    try {
      const { data } = await api.post("/admin/pre-arrival/preview", {
        subject: f.arrival_email_subject, html: f.arrival_email_html,
      });
      setPreview(data);
    } catch (e) { toast.error("Preview failed"); }
  }

  return (
    <div className="space-y-8">
      <div className="border border-soft_border bg-white p-6 md:p-10">
        <div className="overline">Automated welcome</div>
        <h3 className="font-serif text-2xl mt-2 mb-4">Pre-arrival email (T-48h)</h3>
        <p className="text-sm text-ionian/70 mb-6 max-w-2xl leading-relaxed">
          Sent automatically to every paid guest a set number of hours before their check-in. Use placeholders in curly braces —
          <code className="mx-1 text-brass">{"{guest_name}"}</code>,
          <code className="mx-1 text-brass">{"{villa_name}"}</code>,
          <code className="mx-1 text-brass">{"{checkin}"}</code>,
          <code className="mx-1 text-brass">{"{checkout}"}</code>,
          <code className="mx-1 text-brass">{"{nights}"}</code>,
          <code className="mx-1 text-brass">{"{guests}"}</code> — and edit the placeholder Wi-Fi, address and phone numbers below to your real values before your first guest arrives.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              data-testid="arrival-enabled"
              checked={!!f.arrival_email_enabled}
              onChange={e => setF({ ...f, arrival_email_enabled: e.target.checked })}
            />
            Send automatically
          </label>
          <div>
            <Label>Hours before check-in</Label>
            <Input
              data-testid="arrival-lead-hours"
              type="number"
              min="1"
              max="336"
              value={f.arrival_email_lead_hours}
              onChange={e => setF({ ...f, arrival_email_lead_hours: e.target.value })}
            />
          </div>
          <div>
            <Label>Subject line</Label>
            <Input
              data-testid="arrival-subject"
              value={f.arrival_email_subject}
              onChange={e => setF({ ...f, arrival_email_subject: e.target.value })}
            />
          </div>
        </div>

        <Label>Email body (HTML — placeholders in {"{braces}"} auto-fill per guest)</Label>
        <textarea
          data-testid="arrival-html"
          rows={18}
          className="mt-2 w-full font-mono text-xs border border-soft_border p-4 bg-stone_white"
          value={f.arrival_email_html}
          onChange={e => setF({ ...f, arrival_email_html: e.target.value })}
        />

        <div className="mt-6 flex flex-wrap gap-3">
          <Button data-testid="arrival-save" onClick={save} disabled={saving} className="btn-ionian">
            {saving ? "Saving…" : "Save template"}
          </Button>
          <Button data-testid="arrival-preview" variant="outline" onClick={doPreview}>Preview</Button>
        </div>
      </div>

      <RescuePanel settings={settings} onRefresh={onRefresh} />
      {preview && (
        <div className="border border-soft_border bg-white p-6 md:p-10">
          <div className="overline">Preview (Sample Guest, 7 nights)</div>
          <div className="text-sm text-ionian/70 mt-2 mb-4">Subject: <span className="font-medium">{preview.subject}</span></div>
          <div
            data-testid="arrival-preview-html"
            className="border border-soft_border p-4 bg-stone_white"
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        </div>
      )}
    </div>
  );
}

function ArrivalsToday({ arrivals, onRefresh }) {
  const buckets = ["arriving_today", "arriving_tomorrow", "in_house", "departing_today", "upcoming"];
  const labels = {
    arriving_today: "Arriving today",
    arriving_tomorrow: "Arriving tomorrow",
    in_house: "In-house now",
    departing_today: "Departing today",
    upcoming: "Later this fortnight",
  };
  const grouped = buckets.map(b => [b, arrivals.filter(a => a.bucket === b)]);
  async function sendArrival(id) {
    try { await api.post(`/admin/bookings/${id}/send-arrival`); toast.success("Pre-arrival email sent"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <div className="space-y-8" data-testid="arrivals-panel">
      {arrivals.length === 0 && (
        <div className="border border-soft_border bg-white p-10 text-center text-ionian/50">No upcoming arrivals in the next 14 days.</div>
      )}
      {grouped.map(([bucket, items]) => items.length > 0 && (
        <div key={bucket}>
          <div className="overline mb-4">{labels[bucket]} · {items.length}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map(a => (
              <div key={a.id} className="border border-soft_border bg-white p-5" data-testid={`arrival-card-${a.id}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-serif text-2xl">{a.guest_name}</div>
                  <div className="text-xs text-ionian/50">#{a.id.slice(0,8)}</div>
                </div>
                <div className="text-sm text-ionian/70 mt-1">
                  {a.checkin} → {a.checkout} · {a.nights}n · {a.guests} guest{a.guests !== 1 ? "s" : ""}
                </div>
                <div className="mt-4 space-y-1 text-sm">
                  <div><a href={`tel:${a.guest_phone}`} className="text-teal hover:underline">{a.guest_phone || "—"}</a></div>
                  <div><a href={`mailto:${a.guest_email}`} className="text-ionian/70 hover:underline">{a.guest_email}</a></div>
                </div>
                {a.notes && <div className="mt-3 text-xs text-ionian/60 italic border-l-2 border-brass pl-3">{a.notes}</div>}
                <div className="mt-4 flex flex-wrap gap-2">
                  {(bucket === "arriving_today" || bucket === "arriving_tomorrow" || bucket === "upcoming") && (
                    <Button
                      size="sm"
                      variant={a.arrival_email_sent ? "ghost" : "outline"}
                      onClick={() => sendArrival(a.id)}
                      data-testid={`arrival-send-${a.id}`}
                    >
                      {a.arrival_email_sent ? "Resend arrival email" : "Send arrival email"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferralsPanel({ referrals, onRefresh }) {
  const [f, setF] = useState({ guest_name: "", guest_email: "", discount_eur: 150, max_uses: 1 });
  async function add() {
    try {
      await api.post("/admin/referrals", {
        guest_name: f.guest_name || undefined,
        guest_email: f.guest_email || undefined,
        discount_eur: Number(f.discount_eur),
        max_uses: Number(f.max_uses),
      });
      setF({ guest_name: "", guest_email: "", discount_eur: 150, max_uses: 1 });
      toast.success("Referral code created");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function toggle(code, active) {
    try { await api.patch(`/admin/referrals/${code}`, { active: !active }); onRefresh(); }
    catch { toast.error("Failed"); }
  }
  async function remove(code) {
    if (!window.confirm(`Delete referral ${code}?`)) return;
    try { await api.delete(`/admin/referrals/${code}`); onRefresh(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  }
  async function copy(text) {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch {}
  }
  return (
    <div className="space-y-8">
      <div className="border border-soft_border bg-white p-6 md:p-8">
        <div className="overline">Create referral</div>
        <h3 className="font-serif text-2xl mt-2 mb-4">New code</h3>
        <p className="text-sm text-ionian/70 mb-6 max-w-2xl">
          Codes are also generated automatically for every paid guest — those are for them to share with friends. Use this form only if you want to create a bespoke code for a partner or press outreach.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div><Label>Recipient name (optional)</Label><Input data-testid="ref-name" value={f.guest_name} onChange={e=>setF({...f,guest_name:e.target.value})} /></div>
          <div><Label>Recipient email (optional)</Label><Input data-testid="ref-email" value={f.guest_email} onChange={e=>setF({...f,guest_email:e.target.value})} /></div>
          <div><Label>Discount (€)</Label><Input data-testid="ref-discount" type="number" min="0" value={f.discount_eur} onChange={e=>setF({...f,discount_eur:e.target.value})} /></div>
          <div><Label>Max uses</Label><Input data-testid="ref-max" type="number" min="1" value={f.max_uses} onChange={e=>setF({...f,max_uses:e.target.value})} /></div>
          <Button data-testid="ref-add" onClick={add} className="btn-ionian">Create code</Button>
        </div>
      </div>

      <div className="border border-soft_border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {referrals.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-ionian/50 py-10">No referral codes yet.</TableCell></TableRow>}
            {referrals.map(r => (
              <TableRow key={r.code}>
                <TableCell className="font-mono">{r.code}</TableCell>
                <TableCell>
                  <div className="text-sm">{r.owner_name || "—"}</div>
                  <div className="text-xs text-ionian/50">{r.owner_email || (r.source === "auto_paid" ? "auto (paid guest)" : "manual")}</div>
                </TableCell>
                <TableCell>€{Number(r.discount_eur).toFixed(0)}</TableCell>
                <TableCell>{r.uses_count || 0} / {r.max_uses}</TableCell>
                <TableCell className="text-xs">{r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell><Badge variant={r.active ? "default" : "outline"}>{r.active ? "active" : "inactive"}</Badge></TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="ghost" onClick={()=>copy(r.code)}>Copy</Button>
                  <Button size="sm" variant="outline" onClick={()=>toggle(r.code, r.active)}>{r.active ? "Disable" : "Enable"}</Button>
                  <Button size="sm" variant="ghost" onClick={()=>remove(r.code)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function MailingListPanel({ subscribers, onRefresh }) {
  const [q, setQ] = useState("");
  const filtered = subscribers.filter(s =>
    !q || s.email?.toLowerCase().includes(q.toLowerCase()) || (s.name || "").toLowerCase().includes(q.toLowerCase())
  );
  async function remove(email) {
    if (!window.confirm(`Remove ${email} from the mailing list?`)) return;
    try {
      await api.delete(`/admin/newsletter/${encodeURIComponent(email)}`);
      toast.success("Removed");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function copyAll() {
    const emails = filtered.filter(s => s.active !== false).map(s => s.email).join(", ");
    try { await navigator.clipboard.writeText(emails); toast.success(`${filtered.length} email(s) copied`); }
    catch { toast.error("Clipboard blocked — use CSV export"); }
  }
  async function exportCsv() {
    // Fetch CSV via authorised call, then trigger a download client-side.
    try {
      const res = await api.get("/admin/newsletter.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "ithaca-newsletter.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  }

  return (
    <div className="space-y-6" data-testid="mailing-panel">
      <BroadcastComposer />

      <div className="border border-soft_border bg-white p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline">Mailing list</div>
            <h3 className="font-serif text-2xl mt-2">{subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}</h3>
            <p className="text-sm text-ionian/60 mt-1 max-w-lg">Guests and prospects who opted in from the home page. Export the list to send letters through Mailchimp, Substack, or any provider you like.</p>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="mailing-search"
              placeholder="Search name or email"
              value={q}
              onChange={e => setQ(e.target.value)}
              className="w-56"
            />
            <Button variant="outline" data-testid="mailing-copy" onClick={copyAll}>Copy emails</Button>
            <Button className="btn-ionian" data-testid="mailing-export" onClick={exportCsv}>Export CSV</Button>
          </div>
        </div>
      </div>

      <div className="border border-soft_border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Lang</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-ionian/50 py-10">
                {subscribers.length === 0 ? "No subscribers yet." : "No matches."}
              </TableCell></TableRow>
            )}
            {filtered.map(s => (
              <TableRow key={s.email}>
                <TableCell className="font-medium">{s.email}</TableCell>
                <TableCell>{s.name || "—"}</TableCell>
                <TableCell className="text-xs uppercase">{s.language || "en"}</TableCell>
                <TableCell className="text-xs text-ionian/60">{s.source || "—"}</TableCell>
                <TableCell className="text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(s.email)}>Remove</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function BroadcastComposer() {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [sel, setSel] = useState("late_summer");
  const [f, setF] = useState({ subject: "", header: "", eyebrow: "", html: "", language: "all" });
  const [preview, setPreview] = useState(null);
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, h] = await Promise.all([
          api.get("/admin/newsletter/templates"),
          api.get("/admin/newsletter/broadcasts"),
        ]);
        setTemplates(t.data);
        setHistory(h.data);
        const first = t.data.find(x => x.id === sel) || t.data[0];
        if (first) setF({ subject: first.subject, header: first.header, eyebrow: first.eyebrow, html: first.html, language: "all" });
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickTemplate(id) {
    setSel(id);
    const t = templates.find(x => x.id === id);
    if (t) setF({ subject: t.subject, header: t.header, eyebrow: t.eyebrow, html: t.html, language: f.language });
  }

  async function doPreview() {
    try {
      const { data } = await api.post("/admin/newsletter/preview", {
        subject: f.subject, header: f.header, eyebrow: f.eyebrow, html: f.html,
      });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  }

  async function checkAudience() {
    try {
      const { data } = await api.post("/admin/newsletter/broadcast", {
        subject: f.subject || "n/a", html: f.html || "<p></p>", language: f.language, dry_run: true,
      });
      setAudience(data.audience);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function send() {
    if (!window.confirm(`Send this letter to ${audience ?? "all"} subscribers${f.language !== "all" ? ` (${f.language.toUpperCase()} only)` : ""}? This cannot be un-sent.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/broadcast", {
        subject: f.subject, header: f.header, eyebrow: f.eyebrow, html: f.html, language: f.language, dry_run: false,
      });
      toast.success(`Sending to ${data.audience} subscriber${data.audience === 1 ? "" : "s"}…`);
      // Refresh history after a short delay
      setTimeout(async () => {
        try { const h = await api.get("/admin/newsletter/broadcasts"); setHistory(h.data); } catch {}
      }, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setSending(false);
  }

  return (
    <div className="space-y-6">
      <div className="border border-soft_border bg-white p-6 md:p-8" data-testid="broadcast-composer">
        <div className="overline">Compose a letter</div>
        <h3 className="font-serif text-2xl mt-2 mb-2">The letter from the garden</h3>
        <p className="text-sm text-ionian/70 mb-6 max-w-2xl leading-relaxed">
          Pick a template, edit the copy, preview it, and send to your list. Use{" "}
          <code className="text-brass">{"{name}"}</code>,{" "}
          <code className="text-brass">{"{villa_name}"}</code>,{" "}
          <code className="text-brass">{"{early_autumn_from}"}</code>,{" "}
          <code className="text-brass">{"{early_autumn_to}"}</code>{" "}
          — these are filled in for each subscriber.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="md:col-span-2">
            <Label>Template</Label>
            <select
              data-testid="broadcast-template"
              value={sel}
              onChange={e => pickTemplate(e.target.value)}
              className="editorial-input w-full"
            >
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Audience</Label>
            <select
              data-testid="broadcast-audience"
              value={f.language}
              onChange={e => setF({ ...f, language: e.target.value })}
              className="editorial-input w-full"
            >
              <option value="all">All subscribers</option>
              <option value="en">English only</option>
              <option value="gr">Greek only</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={checkAudience} data-testid="broadcast-audience-check">
              {audience === null ? "Count audience" : `${audience} subscriber${audience === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Eyebrow (small line above the heading)</Label>
            <Input data-testid="broadcast-eyebrow" value={f.eyebrow || ""} onChange={e => setF({ ...f, eyebrow: e.target.value })} />
          </div>
          <div>
            <Label>Heading</Label>
            <Input data-testid="broadcast-header" value={f.header || ""} onChange={e => setF({ ...f, header: e.target.value })} />
          </div>
        </div>
        <div className="mt-4">
          <Label>Subject line</Label>
          <Input data-testid="broadcast-subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} />
        </div>
        <div className="mt-4">
          <Label>Letter body (HTML — placeholders auto-fill per subscriber)</Label>
          <textarea
            data-testid="broadcast-html"
            rows={16}
            className="mt-2 w-full font-mono text-xs border border-soft_border p-4 bg-stone_white"
            value={f.html}
            onChange={e => setF({ ...f, html: e.target.value })}
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button data-testid="broadcast-preview" variant="outline" onClick={doPreview}>Preview</Button>
          <Button data-testid="broadcast-send" onClick={send} disabled={sending || !f.subject || !f.html} className="btn-ionian">
            {sending ? "Sending…" : "Send letter"}
          </Button>
        </div>
      </div>

      {preview && (
        <div className="border border-soft_border bg-white p-6 md:p-8">
          <div className="overline">Preview (Sample Guest)</div>
          <div className="text-sm text-ionian/70 mt-2 mb-4">Subject: <span className="font-medium">{preview.subject}</span></div>
          <div data-testid="broadcast-preview-html" className="border border-soft_border p-4 bg-stone_white" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}

      {history.length > 0 && (
        <div className="border border-soft_border bg-white">
          <div className="p-6 border-b border-soft_border overline">Previous broadcasts</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Audience</TableHead>
                <TableHead>Delivered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map(b => (
                <TableRow key={b.id}>
                  <TableCell className="text-xs">{b.sent_at ? new Date(b.sent_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="max-w-md">{b.subject}</TableCell>
                  <TableCell>{b.audience} <span className="text-xs text-ionian/50">({b.language})</span></TableCell>
                  <TableCell className="text-xs">
                    {b.sent != null ? <span className="text-teal">✓ {b.sent}</span> : "sending…"}
                    {b.failed ? <span className="text-terracotta ml-2">✗ {b.failed}</span> : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


function RescuePanel({ settings, onRefresh }) {
  const [f, setF] = useState({
    rescue_email_enabled: settings.rescue_email_enabled ?? true,
    rescue_email_delay_minutes: settings.rescue_email_delay_minutes ?? 90,
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        rescue_email_enabled: !!f.rescue_email_enabled,
        rescue_email_delay_minutes: Number(f.rescue_email_delay_minutes) || 90,
      });
      toast.success("Rescue-email settings saved");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  }
  return (
    <div className="border border-soft_border bg-white p-6 md:p-10" data-testid="rescue-panel">
      <div className="overline">Automated rescue</div>
      <h3 className="font-serif text-2xl mt-2 mb-4">Exit rescue email</h3>
      <p className="text-sm text-ionian/70 mb-6 max-w-2xl leading-relaxed">
        If a guest opens a Stripe checkout page but never completes their payment, we can send them a soft "your dates are still held" nudge with a link to resume where they left off. Fires once per booking, up to 48 hours after they started.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            data-testid="rescue-enabled"
            checked={!!f.rescue_email_enabled}
            onChange={e => setF({ ...f, rescue_email_enabled: e.target.checked })}
          />
          Send automatically
        </label>
        <div>
          <Label>Delay after checkout starts (minutes)</Label>
          <Input
            data-testid="rescue-delay"
            type="number"
            min="15"
            max="720"
            value={f.rescue_email_delay_minutes}
            onChange={e => setF({ ...f, rescue_email_delay_minutes: e.target.value })}
          />
        </div>
        <Button data-testid="rescue-save" onClick={save} disabled={saving} className="btn-ionian">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}


function WaitlistPanel({ waitlist, onRefresh }) {
  const active = waitlist.filter(w => (w.status || "active") === "active");
  const notified = waitlist.filter(w => w.status === "notified");
  async function notify(id) {
    if (!window.confirm("Send the 'your dates opened up' email to this guest now?")) return;
    try { await api.post(`/admin/waitlist/${id}/notify`); toast.success("Sent"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    if (!window.confirm("Remove this waitlist entry?")) return;
    try { await api.delete(`/admin/waitlist/${id}`); toast.success("Removed"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  return (
    <div className="space-y-6" data-testid="waitlist-admin-panel">
      <div className="border border-soft_border bg-white p-6 md:p-8">
        <div className="overline">Waitlist</div>
        <h3 className="font-serif text-2xl mt-2">{active.length} guest{active.length === 1 ? "" : "s"} waiting</h3>
        <p className="text-sm text-ionian/60 mt-1 max-w-lg">
          Guests are notified automatically the moment a booking cancels, expires or you unblock their dates. Use "Notify" only if you want to tell a specific guest that their week is about to open.
        </p>
      </div>

      <div className="border border-soft_border bg-white">
        <div className="p-4 overline">Waiting</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dates</TableHead>
              <TableHead>Guest</TableHead>
              <TableHead>Guests</TableHead>
              <TableHead>Flex</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {active.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-ionian/50 py-10">Nobody is waiting.</TableCell></TableRow>}
            {active.map(w => (
              <TableRow key={w.id}>
                <TableCell className="font-mono text-xs">{w.checkin} → {w.checkout}</TableCell>
                <TableCell>
                  <div className="text-sm">{w.name}</div>
                  <div className="text-xs text-ionian/50">{w.email}{w.phone ? ` · ${w.phone}` : ""}</div>
                  {w.notes ? <div className="text-xs text-ionian/60 italic mt-1">{w.notes}</div> : null}
                </TableCell>
                <TableCell>{w.guests}</TableCell>
                <TableCell className="text-xs">±{w.flexibility_days || 0}d</TableCell>
                <TableCell className="text-xs">{w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" data-testid={`waitlist-notify-${w.id}`} onClick={() => notify(w.id)}>Notify now</Button>
                  <Button size="sm" variant="ghost" data-testid={`waitlist-remove-${w.id}`} onClick={() => remove(w.id)}>Remove</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {notified.length > 0 && (
        <div className="border border-soft_border bg-white">
          <div className="p-4 overline">Notified</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dates</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Notified for</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notified.map(w => (
                <TableRow key={w.id}>
                  <TableCell className="font-mono text-xs">{w.checkin} → {w.checkout}</TableCell>
                  <TableCell>
                    <div className="text-sm">{w.name}</div>
                    <div className="text-xs text-ionian/50">{w.email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{w.notified_window_checkin || "—"} → {w.notified_window_checkout || "—"}</TableCell>
                  <TableCell className="text-xs">{w.notified_at ? new Date(w.notified_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(w.id)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


const AMEN_LABELS = {
  additional_bedroom: "Additional bedroom",
  garage_parking: "Garage parking",
  pool: "Pool",
  jacuzzi: "Jacuzzi",
  gym: "Gym",
  chef: "Private chef",
  breakfast: "Breakfast service",
};

function ReviewsPanel({ reviews, onRefresh }) {
  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await onRefresh();
      toast.success("Reviews refreshed");
    } catch { toast.error("Refresh failed"); }
    setRefreshing(false);
  }
  useEffect(() => {
    api.get("/admin/reviews/amenity-stats").then(r => setStats(r.data)).catch(() => setStats(null));
  }, [reviews.length]);

  async function loadLinks() {
    setLinksLoading(true);
    try {
      const { data } = await api.get("/admin/review-links");
      setLinks(data || []);
    } catch { toast.error("Could not load review links"); }
    setLinksLoading(false);
  }
  async function copyToClipboard(url, guestName) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied link for ${guestName}`);
    } catch { toast.error("Could not copy"); }
  }

  async function togglePublish(r) {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { published: !r.published });
      toast.success(!r.published ? "Published to home page" : "Unpublished");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function saveQuote(id, quote, display_name) {
    try {
      await api.patch(`/admin/reviews/${id}`, { quote, display_name });
      toast.success("Saved");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/admin/reviews/${id}`); onRefresh(); toast.success("Deleted"); }
    catch (e) { toast.error("Failed"); }
  }

  const maxCount = stats ? Math.max(1, ...Object.values(stats.counts)) : 1;

  return (
    <div className="space-y-8" data-testid="reviews-panel">
      {/* Past-guest review links */}
      <div className="border border-soft_border bg-white p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Manual review requests</div>
            <h3 className="font-serif text-2xl mt-2">Past-guest review links</h3>
            <p className="text-sm text-ionian/70 mt-2 max-w-2xl">
              One-click copy the working review URL for any past guest — useful when the auto-sent email was truncated or filtered to spam.
              Tokens are auto-minted if missing.
            </p>
          </div>
          <Button data-testid="review-links-load" onClick={loadLinks} disabled={linksLoading} variant="outline">
            {linksLoading ? "Loading…" : links == null ? "Load past guests" : "Refresh"}
          </Button>
        </div>
        {links != null && (
          links.length === 0 ? (
            <p className="text-sm text-ionian/50 italic mt-6">No past stays yet.</p>
          ) : (
            <Table className="mt-6">
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Stay</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Review link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map(l => (
                  <TableRow key={l.booking_id} data-testid={`review-link-${l.booking_id}`}>
                    <TableCell className="font-medium">{l.guest_name}</TableCell>
                    <TableCell className="text-sm tabular-nums">{l.checkin} → {l.checkout}</TableCell>
                    <TableCell className="text-xs text-ionian/70">{l.guest_email}</TableCell>
                    <TableCell>
                      {l.already_submitted
                        ? <Badge variant="outline" className="text-olive border-olive">Submitted</Badge>
                        : l.review_email_sent
                          ? <Badge variant="outline">Email sent</Badge>
                          : <Badge variant="outline" className="text-terracotta border-terracotta">Not sent</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(l.review_url, l.guest_name)}>
                        Copy link
                      </Button>
                      <a href={l.review_url} target="_blank" rel="noreferrer" className="ml-2 text-xs text-brass hover:underline">Open</a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </div>

      {/* Amenity heatmap */}
      {stats && (
        <div className="border border-soft_border bg-white p-6 md:p-8">
          <div className="overline">Guest wishlist</div>
          <h3 className="font-serif text-2xl mt-2 mb-1">What guests are asking for</h3>
          <p className="text-sm text-ionian/60 mb-6">Based on {stats.total_reviews} response{stats.total_reviews === 1 ? "" : "s"}. Read this before you plan next season's investments.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {Object.entries(stats.counts).map(([k, n]) => (
              <div key={k}>
                <div className="flex items-baseline justify-between text-sm">
                  <span>{AMEN_LABELS[k] || k}</span>
                  <span className="font-mono text-xs text-ionian/60">{n}</span>
                </div>
                <div className="mt-1 h-1 bg-soft_border">
                  <div className="h-1 bg-brass" style={{ width: `${(n / maxCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          {stats.other && stats.other.length > 0 && (
            <div className="mt-8">
              <div className="overline mb-3">Other suggestions</div>
              <ul className="space-y-2 text-sm text-ionian/80">
                {stats.other.slice(0, 8).map((o, i) => (
                  <li key={i} className="border-l-2 border-brass pl-3 italic">
                    "{o.note}" <span className="not-italic text-xs text-ionian/50">— {o.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Reviews table */}
      <div className="border border-soft_border bg-white">
        <div className="p-4 flex items-center justify-between gap-4">
          <div className="overline">All reviews ({reviews.length})</div>
          <Button
            size="sm"
            variant="outline"
            data-testid="reviews-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
        {reviews.length === 0 ? (
          <div className="p-10 text-center text-ionian/50">No reviews yet.</div>
        ) : (
          <div className="divide-y divide-soft_border">
            {reviews.map(r => (
              <ReviewRow key={r.id} r={r} onTogglePublish={togglePublish} onSaveQuote={saveQuote} onRemove={remove} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ r, onTogglePublish, onSaveQuote, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [quote, setQuote] = useState(r.quote || "");
  const [name, setName] = useState(r.display_name || "");
  return (
    <div className="p-5" data-testid={`review-row-${r.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-baseline gap-3">
            <span className="text-brass">{"★".repeat(r.rating)}<span className="text-ionian/25">{"★".repeat(5 - r.rating)}</span></span>
            <span className="font-medium">{r.display_name || "Anonymous"}</span>
            <span className="text-xs text-ionian/50">{r.checkin} → {r.checkout}</span>
            {r.publish_consent ? (
              <Badge variant={r.published ? "default" : "outline"}>{r.published ? "Live on home" : "Consented"}</Badge>
            ) : (
              <Badge variant="outline" className="text-ionian/50">Private only</Badge>
            )}
          </div>
          {editing ? (
            <div className="mt-3 space-y-2">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Display name" />
              <textarea rows={2} value={quote} onChange={e => setQuote(e.target.value)} className="w-full border border-soft_border p-3 text-sm font-serif italic" />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => { onSaveQuote(r.id, quote, name); setEditing(false); }}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            r.quote && <div className="mt-2 font-serif italic text-lg text-ionian/90 leading-snug">"{r.quote}"</div>
          )}
          {r.private_note && (
            <div className="mt-3 text-sm text-ionian/70 border-l-2 border-soft_border pl-3">
              <span className="overline mr-2">Private:</span>{r.private_note}
            </div>
          )}
          {(Object.values(r.amenities || {}).some(Boolean) || r.amenities_other) && (
            <div className="mt-3 text-xs text-ionian/60">
              Wishes: {Object.entries(r.amenities || {}).filter(([, v]) => v).map(([k]) => AMEN_LABELS[k] || k).join(" · ")}
              {r.amenities_other ? <span> · other: <em>{r.amenities_other}</em></span> : null}
            </div>
          )}
          {r.rating >= 5 && (
            <StoryCardTile review={r} />
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {r.publish_consent && r.quote && (
            <Button size="sm" variant={r.published ? "ghost" : "outline"} data-testid={`review-publish-${r.id}`} onClick={() => onTogglePublish(r)}>
              {r.published ? "Unpublish" : "Publish"}
            </Button>
          )}
          {r.quote && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)}>Edit quote</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onRemove(r.id)}>Delete</Button>
        </div>
      </div>
    </div>
  );
}


function StoryCardTile({ review }) {
  const [url, setUrl] = useState(review.story_card_url || null);
  const [regenning, setRegenning] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL || "";

  async function regen() {
    setRegenning(true);
    try {
      const { data } = await api.post(`/admin/reviews/${review.id}/regen-story-card`);
      // Cache-bust so the browser fetches the fresh image
      setUrl(`${data.story_card_url}?t=${Date.now()}`);
      toast.success("Story card regenerated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate");
    } finally {
      setRegenning(false);
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-soft_border" data-testid={`story-card-${review.id}`}>
      <div className="overline text-brass mb-2">Instagram story card</div>
      <div className="flex items-start gap-4 flex-wrap">
        {url ? (
          <a
            href={`${base}${url.split("?")[0]}`}
            download={`review-${review.id}.png`}
            target="_blank"
            rel="noreferrer"
            className="block"
            data-testid={`story-card-download-${review.id}`}
          >
            <img
              src={`${base}${url}`}
              alt="Instagram story card preview"
              className="w-24 h-auto border border-soft_border rounded shadow-sm hover:opacity-90 transition"
              style={{ aspectRatio: "9/16" }}
            />
          </a>
        ) : (
          <div className="w-24 h-40 border border-dashed border-soft_border flex items-center justify-center text-[10px] text-ionian/50 text-center px-2">
            Not generated yet
          </div>
        )}
        <div className="flex-1 min-w-[180px]">
          <p className="text-xs text-ionian/70 leading-relaxed max-w-md">
            A 1080×1920 image ready to post to <span className="font-mono">@ithacagardenvilla</span> story.
            Click the thumbnail to download.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={regen}
            disabled={regenning}
            className="mt-2"
            data-testid={`story-card-regen-${review.id}`}
          >
            {regenning ? "Regenerating…" : url ? "Regenerate" : "Generate"}
          </Button>
        </div>
      </div>
    </div>
  );
}



function RemindersPanel({ reminders, onRefresh }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  async function add() {
    if (!title.trim() || !dueDate) return toast.error("Title and due date required");
    setAdding(true);
    try {
      await api.post("/admin/reminders", { title: title.trim(), due_date: dueDate, note: note.trim() });
      setTitle(""); setDueDate(""); setNote("");
      onRefresh();
      toast.success("Reminder added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setAdding(false); }
  }

  async function toggle(r) {
    try {
      await api.patch(`/admin/reminders/${r.id}`, { done: !r.done });
      onRefresh();
    } catch { toast.error("Failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this reminder?")) return;
    try { await api.delete(`/admin/reminders/${id}`); onRefresh(); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  }

  const daysUntil = (d) => {
    const diff = Math.round((new Date(d) - new Date(today)) / 86400000);
    if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, tone: "text-red-600" };
    if (diff === 0) return { label: "Today", tone: "text-brass" };
    if (diff === 1) return { label: "Tomorrow", tone: "text-ionian" };
    if (diff < 14) return { label: `in ${diff} days`, tone: "text-ionian/70" };
    return { label: `in ${diff} days`, tone: "text-ionian/50" };
  };

  const active = reminders.filter(r => !r.done);
  const done = reminders.filter(r => r.done);

  return (
    <div className="border border-soft_border bg-white p-6 mb-6" data-testid="reminders-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Owner reminders</div>
          <div className="font-serif text-xl text-ionian mt-1">
            {active.length === 0 ? "You're all caught up." : `${active.length} upcoming ${active.length === 1 ? "task" : "tasks"}`}
          </div>
        </div>
      </div>

      {active.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="reminders-active">
          {active.map(r => {
            const d = daysUntil(r.due_date);
            return (
              <div key={r.id} className="flex items-start gap-3 border-l-2 border-brass/60 pl-3 py-2">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => toggle(r)}
                  className="mt-1 h-4 w-4 accent-ionian"
                  data-testid={`reminder-toggle-${r.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="font-medium text-ionian truncate">{r.title}</div>
                    <span className={`text-xs ${d.tone} flex-shrink-0`}>{d.label}</span>
                  </div>
                  {r.note && <div className="text-xs text-ionian/60 mt-1 whitespace-pre-line">{r.note}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`reminder-delete-${r.id}`}>Remove</Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_1fr_auto] gap-3 items-end pt-4 border-t border-soft_border">
        <div>
          <Label>New reminder</Label>
          <Input data-testid="reminder-title" placeholder="e.g. Renew iCal token" value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div>
          <Label>Due date</Label>
          <Input data-testid="reminder-due" type="date" value={dueDate} min={today} onChange={e => setDueDate(e.target.value)} />
        </div>
        <div>
          <Label>Note (optional)</Label>
          <Input data-testid="reminder-note" placeholder="Extra context" value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <Button data-testid="reminder-add" onClick={add} disabled={adding} className="btn-ionian">
          {adding ? "Adding…" : "Add"}
        </Button>
      </div>

      {done.length > 0 && (
        <details className="mt-6">
          <summary className="text-xs uppercase tracking-widest text-ionian/50 cursor-pointer">Done ({done.length})</summary>
          <div className="mt-3 space-y-1" data-testid="reminders-done">
            {done.map(r => (
              <div key={r.id} className="flex items-center gap-3 text-sm text-ionian/50">
                <input
                  type="checkbox"
                  checked={true}
                  onChange={() => toggle(r)}
                  className="h-4 w-4 accent-ionian"
                />
                <span className="line-through truncate flex-1">{r.title}</span>
                <span className="text-xs">{r.due_date}</span>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>×</Button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
